import { LAST_WRECK_CONTENT, type SalvageId, type WreckContent } from '../content';
import { calculateCableTension } from './grapple';
import { addInstability } from './instability';
import { areNavigationClampsReleased, isConnectionPassable } from './power';
import { pressureEqualizationImpulse } from './pressure';
import { calculateRunResult, salvageValue } from './scoring';
import type { GameCommand, GameEvent, GameState, ReduceResult, TutorialStep } from './types';

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.min(maximum, Math.max(minimum, value));

function invariant<T>(value: T | undefined, message: string): T {
  if (value === undefined) throw new Error(`Game-state invariant failed: ${message}`);
  return value;
}

function rejected(state: GameState, command: GameCommand, reason: string): ReduceResult {
  return { state, events: [{ type: 'COMMAND_REJECTED', command: command.type, reason }] };
}

function tutorialAdvance(
  state: GameState,
  step: TutorialStep,
): { readonly state: GameState; readonly event: GameEvent } {
  return {
    state: { ...state, tutorialStep: step, phase: step === 'complete' ? 'active' : state.phase },
    event: { type: 'TUTORIAL_ADVANCED', step },
  };
}

function applyStructuralState(state: GameState): GameState {
  let connections = state.connections;
  if (state.instability.triggered.includes(35)) {
    connections = {
      ...connections,
      'maintenance-corridor': {
        ...invariant(connections['maintenance-corridor'], 'maintenance corridor missing'),
        narrowed: true,
      },
    };
  }
  if (state.instability.triggered.includes(65)) {
    connections = {
      ...connections,
      'cargo-navigation': {
        ...invariant(connections['cargo-navigation'], 'cargo navigation route missing'),
        collapsed: true,
      },
    };
  }
  return connections === state.connections ? state : { ...state, connections };
}

function increaseInstability(
  state: GameState,
  amount: number,
): { readonly state: GameState; readonly events: readonly GameEvent[] } {
  const update = addInstability(state, amount);
  const next = applyStructuralState({
    ...state,
    instability: {
      ...update.instability,
      emergencyRemainingSeconds:
        update.phase === 'emergency' && state.phase !== 'emergency'
          ? LAST_WRECK_CONTENT.tuning.emergencySeconds
          : update.instability.emergencyRemainingSeconds,
    },
    phase: update.phase,
  });
  return { state: next, events: update.events };
}

function finishRun(state: GameState, leftDuringEmergency: boolean): ReduceResult {
  const result = calculateRunResult(state, LAST_WRECK_CONTENT, leftDuringEmergency);
  const ended: GameState = { ...state, phase: 'ended', grapple: null, result };
  return { state: ended, events: [{ type: 'RUN_ENDED', result }] };
}

function tick(state: GameState, seconds: number, content: WreckContent): ReduceResult {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return rejected(state, { type: 'TICK', seconds }, 'Tick duration must be positive.');
  }
  if (state.phase === 'ended') return { state, events: [] };

  const cooledCuts = Object.fromEntries(
    content.cutPoints.map((definition) => {
      const runtime = invariant(state.cuts[definition.id], `cut ${definition.id} missing`);
      return [
        definition.id,
        { ...runtime, heat: Math.max(0, runtime.heat - definition.coolRate * seconds) },
      ];
    }),
  );
  const expiredJumper = state.power.jumper
    ? state.power.jumper.remainingSeconds - seconds <= 0
    : false;
  const nextJumper = state.power.jumper
    ? expiredJumper
      ? null
      : {
          ...state.power.jumper,
          remainingSeconds: state.power.jumper.remainingSeconds - seconds,
        }
    : null;
  let next: GameState = {
    ...state,
    elapsedSeconds: state.elapsedSeconds + seconds,
    scannerCooldownSeconds: Math.max(0, state.scannerCooldownSeconds - seconds),
    toolDisruptionSeconds: Math.max(0, state.toolDisruptionSeconds - seconds),
    cuts: cooledCuts,
    power: { ...state.power, jumper: nextJumper },
  };
  const events: GameEvent[] = [];
  if (expiredJumper && state.power.jumper) {
    events.push({ type: 'JUMPER_EXPIRED', deviceId: state.power.jumper.deviceId });
  }

  if (state.phase === 'emergency') {
    const remaining = Math.max(
      0,
      (state.instability.emergencyRemainingSeconds ?? content.tuning.emergencySeconds) - seconds,
    );
    next = {
      ...next,
      instability: { ...next.instability, emergencyRemainingSeconds: remaining },
    };
    if (remaining === 0) {
      const finished = finishRun(next, false);
      return { state: finished.state, events: [...events, ...finished.events] };
    }
    return { state: next, events };
  }

  const instability = increaseInstability(next, content.tuning.baseInstabilityPerSecond * seconds);
  return { state: instability.state, events: [...events, ...instability.events] };
}

function scan(
  state: GameState,
  command: Extract<GameCommand, { type: 'SCAN' }>,
  content: WreckContent,
): ReduceResult {
  if (state.scannerCooldownSeconds > 0) return rejected(state, command, 'Scanner is cooling down.');
  if (state.toolDisruptionSeconds > 0) return rejected(state, command, 'Tools are disrupted.');
  const revealUntil = state.elapsedSeconds + content.tuning.scanRevealSeconds;
  const adjacentConnections = content.connections.filter(
    (connection) =>
      connection.from === state.currentRoomId || connection.to === state.currentRoomId,
  );
  const connections = { ...state.connections };
  for (const connection of adjacentConnections) {
    connections[connection.id] = {
      ...invariant(connections[connection.id], `connection ${connection.id} missing`),
      revealedUntil: revealUntil,
    };
  }
  const cuts = { ...state.cuts };
  for (const point of content.cutPoints.filter((point) => point.roomId === state.currentRoomId)) {
    cuts[point.id] = {
      ...invariant(cuts[point.id], `cut ${point.id} missing`),
      revealedUntil: revealUntil,
    };
  }
  const salvage = { ...state.salvage };
  for (const item of content.salvage.filter((item) => item.roomId === state.currentRoomId)) {
    salvage[item.id] = { ...salvage[item.id], revealedUntil: revealUntil };
  }
  const rooms = {
    ...state.rooms,
    [state.currentRoomId]: {
      ...state.rooms[state.currentRoomId],
      revealedUntil: revealUntil,
      powerVisible: true,
    },
  };
  const targetWasHidden = state.salvage['navigation-core'].revealedUntil <= state.elapsedSeconds;
  const targetLocated = state.currentRoomId === 'navigation-chamber' && targetWasHidden;
  let next: GameState = {
    ...state,
    rooms,
    connections,
    cuts,
    salvage,
    scannerCooldownSeconds: content.tuning.scanCooldownSeconds,
    stats: {
      ...state.stats,
      scans: state.stats.scans + 1,
      targetFirstLocatedAt: targetLocated ? state.elapsedSeconds : state.stats.targetFirstLocatedAt,
    },
  };
  const events: GameEvent[] = [
    { type: 'SCAN_PULSE', mode: command.mode, roomId: state.currentRoomId },
  ];
  if (targetLocated) events.push({ type: 'TARGET_LOCATED', salvageId: 'navigation-core' });
  if (state.tutorialStep === 'scan-hatch' && state.currentRoomId === 'docking-throat') {
    const tutorial = tutorialAdvance(next, 'cut-hatch');
    next = tutorial.state;
    events.push(tutorial.event);
  }
  return { state: next, events };
}

function updatePressureForConnection(
  state: GameState,
  connectionId: string,
  content: WreckContent,
): { readonly state: GameState; readonly event: GameEvent | null } {
  const connection = content.connections.find((candidate) => candidate.id === connectionId);
  if (!connection) return { state, event: null };
  const fromPressure = state.rooms[connection.from].pressure;
  const toPressure = state.rooms[connection.to].pressure;
  const pressure = pressureEqualizationImpulse(fromPressure, toPressure, connection.normal);
  if (!pressure) return { state, event: null };
  const rooms = {
    ...state.rooms,
    [connection.from]: {
      ...state.rooms[connection.from],
      pressure: pressure.equalizedPressure,
    },
    [connection.to]: {
      ...state.rooms[connection.to],
      pressure: pressure.equalizedPressure,
    },
  };
  return {
    state: { ...state, rooms },
    event: {
      type: 'PRESSURE_EQUALIZED',
      from: connection.from,
      to: connection.to,
      impulse: pressure.fromHighToLow,
    },
  };
}

function cut(
  state: GameState,
  command: Extract<GameCommand, { type: 'CUT' }>,
  content: WreckContent,
): ReduceResult {
  const definition = content.cutPoints.find((point) => point.id === command.cutPointId);
  if (!definition) return rejected(state, command, 'This surface is not an authored cut point.');
  if (definition.roomId !== state.currentRoomId)
    return rejected(state, command, 'Cut point is not in this room.');
  if (state.selectedTool !== 'cutter') return rejected(state, command, 'Select the cutter first.');
  if (state.toolDisruptionSeconds > 0) return rejected(state, command, 'Tools are disrupted.');
  if (!Number.isFinite(command.seconds) || command.seconds <= 0) {
    return rejected(state, command, 'Cut duration must be positive.');
  }
  const runtime = invariant(state.cuts[definition.id], `cut ${definition.id} missing`);
  if (runtime.complete) return rejected(state, command, 'Cut point is already open.');
  if (runtime.revealedUntil <= state.elapsedSeconds) {
    return rejected(state, command, 'Scan to reveal the marked seam first.');
  }
  if (runtime.heat >= definition.heatLimit) {
    return rejected(state, command, 'Cutter must cool before continuing.');
  }

  const alignment = clamp(command.alignment, 0, 1);
  const valid = alignment >= 0.45;
  const progressGain = valid
    ? (command.seconds / definition.durationSeconds) * ((alignment - 0.4) / 0.6)
    : 0;
  const progress = clamp(runtime.progress + progressGain, 0, 1);
  const heat = Math.min(definition.heatLimit, runtime.heat + command.seconds * definition.heatRate);
  const completed = progress >= 1;
  let next: GameState = {
    ...state,
    cuts: {
      ...state.cuts,
      [definition.id]: { ...runtime, progress, heat, complete: completed },
    },
    stats: { ...state.stats, cuts: state.stats.cuts + 1 },
  };
  const events: GameEvent[] = [
    { type: 'CUT_PROGRESS', cutPointId: definition.id, progress, heat, valid },
  ];

  if (definition.target.kind === 'salvage' && alignment < 0.75) {
    const salvage = next.salvage[definition.target.salvageId];
    const damage = (0.75 - alignment) * definition.fragility * command.seconds * 8;
    next = {
      ...next,
      salvage: {
        ...next.salvage,
        [definition.target.salvageId]: {
          ...salvage,
          condition: Math.max(0, salvage.condition - damage),
        },
      },
    };
  }

  if (!completed) {
    if (heat >= definition.heatLimit) {
      next = { ...next, toolDisruptionSeconds: 0.6 };
      events.push({ type: 'CUTTER_OVERHEAT', cutPointId: definition.id });
    }
    return { state: next, events };
  }

  events.push({ type: 'CUT_COMPLETE', cutPointId: definition.id });
  if (definition.target.kind === 'connection') {
    next = {
      ...next,
      connections: {
        ...next.connections,
        [definition.target.connectionId]: {
          ...invariant(
            next.connections[definition.target.connectionId],
            `connection ${definition.target.connectionId} missing`,
          ),
          open: true,
        },
      },
    };
    const pressure = updatePressureForConnection(next, definition.target.connectionId, content);
    next = pressure.state;
    if (pressure.event) events.push(pressure.event);
    const instability = increaseInstability(next, 4);
    next = instability.state;
    events.push(...instability.events);
  } else {
    const salvage = next.salvage[definition.target.salvageId];
    next = {
      ...next,
      salvage: {
        ...next.salvage,
        [definition.target.salvageId]: {
          ...salvage,
          attachmentIdsCut: [...salvage.attachmentIdsCut, definition.id],
        },
      },
    };
  }

  if (state.tutorialStep === 'cut-hatch' && definition.id === 'tutorial-hatch-seam') {
    const tutorial = tutorialAdvance(next, 'grapple-cassette');
    next = tutorial.state;
    events.push(tutorial.event);
  }
  return { state: next, events };
}

export function isSalvageDetached(
  state: GameState,
  salvageId: SalvageId,
  content: WreckContent = LAST_WRECK_CONTENT,
): boolean {
  const definition = content.salvage.find((item) => item.id === salvageId);
  if (!definition) return false;
  const allAttachmentsCut = definition.attachmentIds.every((id) =>
    state.salvage[salvageId].attachmentIdsCut.includes(id),
  );
  return (
    allAttachmentsCut && (salvageId !== 'navigation-core' || areNavigationClampsReleased(state))
  );
}

export function reduceGame(
  state: GameState,
  command: GameCommand,
  content: WreckContent = LAST_WRECK_CONTENT,
): ReduceResult {
  if (state.phase === 'ended' && command.type !== 'TICK') {
    return rejected(state, command, 'Run has ended.');
  }
  switch (command.type) {
    case 'TICK':
      return tick(state, command.seconds, content);
    case 'SELECT_TOOL':
      return { state: { ...state, selectedTool: command.tool }, events: [] };
    case 'SCAN':
      return scan(state, command, content);
    case 'CUT':
      return cut(state, command, content);
    case 'ENTER_ROOM': {
      const definition = content.connections.find(
        (connection) => connection.id === command.viaConnectionId,
      );
      if (!definition) return rejected(state, command, 'Unknown connection.');
      const connectsCurrent =
        (definition.from === state.currentRoomId && definition.to === command.roomId) ||
        (definition.to === state.currentRoomId && definition.from === command.roomId);
      if (!connectsCurrent) return rejected(state, command, 'Connection does not lead there.');
      if (!isConnectionPassable(state, content, definition.id)) {
        return rejected(state, command, 'Route is closed or unpowered.');
      }
      const roomsVisited = state.stats.roomsVisited.includes(command.roomId)
        ? state.stats.roomsVisited
        : [...state.stats.roomsVisited, command.roomId];
      const salvage = state.grapple
        ? {
            ...state.salvage,
            [state.grapple.salvageId]: {
              ...state.salvage[state.grapple.salvageId],
              roomId: command.roomId,
            },
          }
        : state.salvage;
      return {
        state: {
          ...state,
          currentRoomId: command.roomId,
          salvage,
          stats: { ...state.stats, roomsVisited },
        },
        events: [],
      };
    }
    case 'SET_POWER': {
      if (state.currentRoomId !== 'power-control') {
        return rejected(state, command, 'Power buses can only be changed in Power Control.');
      }
      const nextBusState = command.action === 'restore' ? 'restored' : 'isolated';
      if (state.power.buses[command.bus] === nextBusState) return { state, events: [] };
      let next: GameState = {
        ...state,
        power: {
          ...state.power,
          buses: { ...state.power.buses, [command.bus]: nextBusState },
        },
      };
      const events: GameEvent[] = [
        { type: 'POWER_CHANGED', bus: command.bus, state: nextBusState },
      ];
      if (command.action === 'restore') {
        const instability = increaseInstability(next, 3);
        next = instability.state;
        events.push(...instability.events);
      }
      return { state: next, events };
    }
    case 'ISOLATE_ALL_POWER': {
      if (state.currentRoomId !== 'power-control') {
        return rejected(state, command, 'Power buses can only be isolated in Power Control.');
      }
      return {
        state: {
          ...state,
          power: { ...state.power, buses: { A: 'isolated', B: 'isolated' } },
        },
        events: [
          { type: 'POWER_CHANGED', bus: 'A', state: 'isolated' },
          { type: 'POWER_CHANGED', bus: 'B', state: 'isolated' },
        ],
      };
    }
    case 'USE_JUMPER': {
      if (!state.power.hasPortableJumper) {
        return rejected(state, command, 'Bank the tool cassette to unlock the portable jumper.');
      }
      return {
        state: {
          ...state,
          power: {
            ...state.power,
            jumper: { deviceId: command.deviceId, remainingSeconds: 20 },
          },
        },
        events: [{ type: 'JUMPER_STARTED', deviceId: command.deviceId, seconds: 20 }],
      };
    }
    case 'SURGE_MISTAKE': {
      const instability = increaseInstability(state, 5);
      return { state: instability.state, events: instability.events };
    }
    case 'GRAPPLE_ATTACH': {
      if (state.selectedTool !== 'grapple')
        return rejected(state, command, 'Select the grapple first.');
      if (state.grapple) return rejected(state, command, 'Only one grapple is available.');
      if (!command.lineOfSight || command.distance > content.tuning.grappleRange) {
        return rejected(state, command, 'Target is out of range or line of sight.');
      }
      const salvage = state.salvage[command.salvageId];
      if (salvage.banked || salvage.roomId !== state.currentRoomId) {
        return rejected(state, command, 'Salvage is unavailable in this room.');
      }
      if (!isSalvageDetached(state, command.salvageId, content)) {
        return rejected(state, command, 'Salvage is still attached or clamped.');
      }
      let next: GameState = {
        ...state,
        grapple: {
          salvageId: command.salvageId,
          cableLength: Math.max(24, command.distance),
          tension: 0,
          warning: false,
        },
        stats: {
          ...state.stats,
          grappleAttachments: state.stats.grappleAttachments + 1,
        },
      };
      const events: GameEvent[] = [{ type: 'GRAPPLE_ATTACHED', salvageId: command.salvageId }];
      if (state.tutorialStep === 'grapple-cassette' && command.salvageId === 'tool-cassette') {
        const tutorial = tutorialAdvance(next, 'deposit-cassette');
        next = tutorial.state;
        events.push(tutorial.event);
      }
      return { state: next, events };
    }
    case 'GRAPPLE_UPDATE': {
      if (!state.grapple) return rejected(state, command, 'No grapple is attached.');
      const definition = invariant(
        content.salvage.find((item) => item.id === state.grapple?.salvageId),
        `salvage ${state.grapple.salvageId} missing`,
      );
      const cableLength = clamp(state.grapple.cableLength + command.reelDelta, 24, 180);
      const tension = calculateCableTension(
        command.distance,
        cableLength,
        definition.mass,
        command.relativeSpeed,
      );
      const warning = tension >= content.tuning.grappleWarningTension;
      if (tension >= content.tuning.grappleWarningTension * content.tuning.cableBreakMultiplier) {
        return {
          state: {
            ...state,
            grapple: null,
            stats: { ...state.stats, cableBreaks: state.stats.cableBreaks + 1 },
          },
          events: [{ type: 'CABLE_BROKE', salvageId: definition.id }],
        };
      }
      return {
        state: { ...state, grapple: { ...state.grapple, cableLength, tension, warning } },
        events: [{ type: 'GRAPPLE_TENSION', tension, warning }],
      };
    }
    case 'GRAPPLE_RELEASE':
      return { state: { ...state, grapple: null }, events: [] };
    case 'MOVE_GRAPPLED_SALVAGE': {
      if (!state.grapple) return rejected(state, command, 'No grapple is attached.');
      return {
        state: {
          ...state,
          salvage: {
            ...state.salvage,
            [state.grapple.salvageId]: {
              ...state.salvage[state.grapple.salvageId],
              roomId: command.roomId,
            },
          },
        },
        events: [],
      };
    }
    case 'DEPOSIT': {
      if (state.currentRoomId !== content.extractionRoomId) {
        return rejected(state, command, 'Salvage must reach the extraction cradle.');
      }
      if (state.grapple?.salvageId !== command.salvageId) {
        return rejected(state, command, 'Deposit the object currently held by the grapple.');
      }
      const runtime = state.salvage[command.salvageId];
      if (runtime.roomId !== content.extractionRoomId) {
        return rejected(state, command, 'Salvage is not inside the extraction cradle.');
      }
      const definition = invariant(
        content.salvage.find((item) => item.id === command.salvageId),
        `salvage ${command.salvageId} missing`,
      );
      let next: GameState = {
        ...state,
        grapple: null,
        salvage: {
          ...state.salvage,
          [command.salvageId]: { ...runtime, banked: true },
        },
        power: {
          ...state.power,
          hasPortableJumper: state.power.hasPortableJumper || command.salvageId === 'tool-cassette',
        },
        stats: {
          ...state.stats,
          targetExtractedAt:
            command.salvageId === content.requiredSalvageId
              ? state.elapsedSeconds
              : state.stats.targetExtractedAt,
        },
      };
      const events: GameEvent[] = [
        {
          type: 'SALVAGE_BANKED',
          salvageId: command.salvageId,
          value: salvageValue(definition.baseValue, runtime.condition),
        },
      ];
      if (state.tutorialStep === 'deposit-cassette' && command.salvageId === 'tool-cassette') {
        const tutorial = tutorialAdvance(next, 'complete');
        next = tutorial.state;
        events.push(tutorial.event);
      }
      return { state: next, events };
    }
    case 'HARD_IMPACT': {
      const severity = clamp(command.severity, 0, 2);
      const next = {
        ...state,
        toolDisruptionSeconds: Math.max(state.toolDisruptionSeconds, 0.75 * severity),
        stats: { ...state.stats, hardImpacts: state.stats.hardImpacts + 1 },
      };
      const instability = increaseInstability(next, 1.5 * severity);
      return { state: instability.state, events: instability.events };
    }
    case 'LEAVE_WRECK':
      return finishRun(state, state.phase === 'emergency');
  }
}
